import { useEffect, useMemo, useState } from 'react';
import { Pressable, RefreshControl, ScrollView, Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { ActivityIndicator } from 'react-native-paper';
import { useLocalSearchParams, useNavigation, useRouter } from 'expo-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Toast from 'react-native-toast-message';
import Chip from '@/components/General/Chip';
import Callout from '@/components/General/Callout';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import Unicon from '@/components/General/Unicon';
import SolidButton from '@/components/General/SolidButton';
import SelectInput from '@/components/General/inputs/SelectInput';
import PathEditor from '@/components/FollowUp/Paths/PathEditor';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { Mutation } from '@/utils/react-query/Mutations';
import { getPeopleByIds } from '@/data/people';
import useOrganizationEntitlements from '@/utils/hooks/useOrganizationEntitlements';
import { canViewModule, isFreeOrg } from '@/utils/organizationEntitlements';
import { extractFollowUpCode } from '@/data/followUp';
import { showAlert } from '@/utils/showAlert';
import { parseTimeFrame } from '@/utils/dater';
import {
    buildPublishPayload,
    definitionToDraft,
    describeStep,
    stepTitle,
} from '@/utils/followUpPaths';
import {
    getStepActionIcon,
    getEnrollmentStatusMeta,
    getStopReasonLabel,
} from '@/utils/constants/followUp';

/**
 * /follow-up/paths/[followUpPathID] — flat template detail (§25.5): the
 * ordered step preview (action, wait, assignment, deadline), participants
 * with their current-step summaries, and Edit/activate/archive controls.
 *
 * Edits and archive/active toggles all go through follow_up_publish_path
 * with expectedRevision — the sole write path. FOLLOW_UP_CONFLICT means a
 * concurrent publish; the UI offers a reload, never a clobber (§25.13).
 */

const STATUS_OPTIONS = [
    { value: 'ALL', label: 'All participants' },
    { value: 'ACTIVE', label: 'Active' },
    { value: 'COMPLETED', label: 'Finished' },
    { value: 'CANCELLED', label: 'Stopped' },
];

/** Resolve ids referenced inside step configs to display names. */
const useStepNames = (path) => {
    const { personIDs, templateIDs } = useMemo(() => {
        const people = new Set();
        const templates = new Set();
        (path?.steps || []).forEach((step) => {
            const cfg = step?.config || {};
            if (cfg.assignment?.personID) people.add(cfg.assignment.personID);
            (cfg.assignment?.poolMemberIDs || []).forEach((id) => id && people.add(id));
            (step?.poolMembers || []).forEach((m) => m?.personID && people.add(m.personID));
            if (cfg.emailTemplateID) templates.add(cfg.emailTemplateID);
        });
        return { personIDs: [...people], templateIDs: [...templates] };
    }, [path]);

    const { data: people = [] } = useQuery({
        queryKey: ['people', 'byIds', organizationKey(personIDs)],
        queryFn: () => getPeopleByIds(personIDs, ['id', 'firstName', 'lastName']),
        enabled: personIDs.length > 0,
        staleTime: 1000 * 60 * 5,
    });
    const { data: emailTemplates = [] } = Query.emailTemplates.all({
        options: { enabled: templateIDs.length > 0 },
    });

    return useMemo(() => ({
        peopleMap: Object.fromEntries((people || []).map((p) => [
            p.id, `${p.firstName || ''} ${p.lastName || ''}`.trim(),
        ])),
        templateMap: Object.fromEntries((emailTemplates || []).map((t) => [t.id, t.name])),
    }), [people, emailTemplates]);
};

const organizationKey = (ids) => [...ids].sort().join(',');

const namesFor = (step, maps) => ({
    templateName: maps.templateMap[step?.config?.emailTemplateID],
    assigneeName: maps.peopleMap[step?.config?.assignment?.personID],
    poolNames: (step?.config?.assignment?.poolMemberIDs || [])
        .map((id) => maps.peopleMap[id] || (step?.poolMembers || []).find((m) => m?.personID === id)?.name)
        .filter(Boolean),
});

const StepLine = ({ step, index, maps }) => (
    <View style={styles.reqRow}>
        <View style={styles.reqIcon}>
            <Unicon name={getStepActionIcon(step)} size={14} colorVariant="paths" />
        </View>
        <View style={styles.reqBody}>
            <View style={styles.reqTitleRow}>
                <Text style={styles.reqName} numberOfLines={1}>{stepTitle(step, `Step ${index + 1}`)}</Text>
            </View>
            <Text style={styles.reqMeta} numberOfLines={2}>
                {describeStep(step, index, namesFor(step, maps))}
            </Text>
        </View>
    </View>
);

const EnrollmentRow = ({ item, onPress }) => {
    const meta = getEnrollmentStatusMeta(item.status);
    const current = item.currentStep;
    const currentLine = current
        ? `${current.label || 'Step'} — ${
            current.status === 'WAITING' && current.availableAt
                ? `starts ${parseTimeFrame(current.availableAt)}`
                : current.status === 'ACTIVE'
                    ? (current.expiresAt ? `expires ${parseTimeFrame(current.expiresAt)}` : 'no time limit')
                    : 'needs attention'
        }`
        : null;
    return (
        <Pressable
            testID={`path-enrollment-${item.enrollmentID}`}
            accessibilityRole="button"
            onPress={onPress}
            style={styles.enrollRow}
        >
            <View style={styles.enrollBody}>
                <Text style={styles.enrollName} numberOfLines={1}>{item.personName || 'Unknown'}</Text>
                <Text style={styles.enrollMeta} numberOfLines={1}>
                    {[
                        currentLine,
                        item.startedAt ? `Started ${parseTimeFrame(item.startedAt)}` : null,
                        item.completedAt ? `Finished ${parseTimeFrame(item.completedAt)}` : null,
                        item.stopReason ? getStopReasonLabel(item.stopReason) : null,
                    ].filter(Boolean).join(' · ')}
                </Text>
            </View>
            {!!item.needsAttention && (
                <Chip icon="alert-circle-outline" colorVariant="warning">Needs attention</Chip>
            )}
            <Chip icon={meta.icon} colorVariant={meta.colorVariant}>{meta.label}</Chip>
            <Unicon name="chevron-forward-outline" size={16} colorVariant="textTertiary" />
        </Pressable>
    );
};

export default function FollowUpPathDetailScreen() {
    const router = useRouter();
    const navigation = useNavigation();
    const queryClient = useQueryClient();
    const { followUpPathID } = useLocalSearchParams();
    const { data: loggedInUser } = Query.people.loggedInUser();
    const { entitlements } = useOrganizationEntitlements();

    const organizationID = loggedInUser?.organizationID;
    const canView = !!loggedInUser?.can?.('View Follow Up');
    const canManage = !!loggedInUser?.can?.('Manage Follow Up');
    const moduleAllowed = canViewModule(entitlements, 'paths');
    const gated = !!loggedInUser && canView && moduleAllowed;

    const [editing, setEditing] = useState(false);
    const [statusFilter, setStatusFilter] = useState('ACTIVE');

    const { data: path, isLoading, isError, error, isRefetching, refetch } = Query.followUp.path({
        organizationID,
        pathID: followUpPathID,
        options: { enabled: gated },
    });

    const enrollmentFilter = useMemo(() => {
        const f = { pathID: followUpPathID };
        if (statusFilter) f.status = statusFilter;
        return f;
    }, [followUpPathID, statusFilter]);

    const {
        data: enrollPages, isLoading: enrollLoading,
        hasNextPage, fetchNextPage, isFetchingNextPage,
    } = Query.followUp.enrollments({
        organizationID,
        filter: enrollmentFilter,
        options: { enabled: gated && !!followUpPathID },
    });

    const enrollments = useMemo(
        () => (enrollPages?.pages || []).flatMap((page) => page?.rows || []),
        [enrollPages]
    );

    const nameMaps = useStepNames(path);

    const flagMutation = Mutation.followUp.publishPath({
        onSuccess: (data) => {
            Toast.show({ type: 'success', text2: 'Path updated' });
            return data;
        },
        onError: (mutationError) => {
            const code = mutationError?.followUpCode ?? extractFollowUpCode(mutationError);
            if (code === 'FOLLOW_UP_CONFLICT') {
                showAlert({
                    title: 'Definition changed elsewhere',
                    subtitle: 'Someone else published a newer revision. Reload the latest version and try again.',
                    cancelText: 'Cancel',
                    confirmText: 'Reload',
                    onConfirm: () => refetch(),
                });
                return;
            }
            if (!mutationError?.expected) {
                Toast.show({ type: 'error', text2: 'Failed to update the path.' });
            }
        },
    });

    // Archive/active toggles republish the SAME definition with one flag
    // changed — publish is the sole write path (spec §12.1/§15.4).
    const toggleFlag = (flag, value, label) => {
        const draft = definitionToDraft(path);
        draft[flag] = value;
        const { errors, payload } = buildPublishPayload(draft);
        if (!payload) {
            Toast.show({ type: 'error', text2: errors[0] || 'Cannot update this path.' });
            return;
        }
        flagMutation.mutate({ organizationID, payload, label });
    };

    useEffect(() => {
        navigation.setOptions({ title: path?.name || 'Path' });
    }, [navigation, path?.name]);

    const refresh = () => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.path(organizationID, followUpPathID) });
        queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.enrollments(organizationID, JSON.stringify(enrollmentFilter)) });
    };

    if (loggedInUser && (!canView || !moduleAllowed)) {
        return (
            <View style={styles.deniedWrap}>
                <EmptyListPlaceholder
                    type="404"
                    spacing={40}
                    message={!moduleAllowed && isFreeOrg(entitlements)
                        ? 'Follow Up is not available on this plan'
                        : "You don't have permission to view Follow Up paths."}
                />
            </View>
        );
    }

    if (isError) {
        const code = extractFollowUpCode(error) || error?.followUpCode;
        return (
            <View style={styles.deniedWrap}>
                <EmptyListPlaceholder
                    type="404"
                    spacing={40}
                    message={code === 'FOLLOW_UP_DENIED'
                        ? "You don't have permission to view this path."
                        : code === 'FOLLOW_UP_NOT_FOUND'
                            ? "That path couldn't be found. It may have been removed."
                            : 'Something went wrong loading the path. Pull to refresh to try again.'}
                />
            </View>
        );
    }

    if (editing && canManage && path) {
        return (
            <PathEditor
                organizationID={organizationID}
                path={path}
                onCancel={() => setEditing(false)}
                onReload={() => refetch()}
                onPublished={() => setEditing(false)}
            />
        );
    }

    const steps = path?.steps || [];
    const openCount = enrollments.filter((e) => e.status === 'ACTIVE').length;
    const attentionCount = enrollments.filter((e) => e.needsAttention).length;

    return (
        <View style={styles.container}>
            <ScrollView
                style={styles.scroll}
                contentContainerStyle={styles.content}
                keyboardShouldPersistTaps="handled"
                refreshControl={<RefreshControl refreshing={isRefetching} onRefresh={refresh} />}
            >
                {isLoading || !path ? (
                    <View style={styles.loadingWrap}><ActivityIndicator size="large" /></View>
                ) : (
                    <>
                        <View style={styles.header}>
                            <View style={styles.titleRow}>
                                <Text style={styles.title} numberOfLines={2}>{path.name}</Text>
                                <Chip icon="pricetag-outline" colorVariant="textTertiary">{`rev ${path.revision}`}</Chip>
                            </View>
                            <View style={styles.chipsRow}>
                                {path.isActive === false && !path.isArchived && (
                                    <Chip icon="pause-outline" colorVariant="textSecondary">Inactive</Chip>
                                )}
                                {!!path.isArchived && (
                                    <Chip icon="archive-outline" colorVariant="textTertiary">Archived</Chip>
                                )}
                                {!!path.latestRevision?.publishedAt && (
                                    <Text style={styles.metaLine}>
                                        {`Published ${parseTimeFrame(path.latestRevision.publishedAt)}`}
                                    </Text>
                                )}
                            </View>
                            {!!path.description && (
                                <Text style={styles.description}>{path.description}</Text>
                            )}
                        </View>

                        {canManage && (
                            <View style={styles.actionsRow}>
                                <SolidButton
                                    testID="path-edit"
                                    size="sm"
                                    iconName="create-outline"
                                    text="Edit"
                                    onPress={() => setEditing(true)}
                                />
                                {path.isActive !== false ? (
                                    <SolidButton
                                        testID="path-deactivate"
                                        size="sm"
                                        variant="outlined"
                                        type="secondary"
                                        iconName="pause-outline"
                                        text="Deactivate"
                                        disabled={flagMutation.isPending}
                                        onPress={() => toggleFlag('isActive', false)}
                                    />
                                ) : (
                                    <SolidButton
                                        testID="path-activate"
                                        size="sm"
                                        variant="outlined"
                                        type="secondary"
                                        iconName="play-outline"
                                        text="Activate"
                                        disabled={flagMutation.isPending}
                                        onPress={() => toggleFlag('isActive', true)}
                                    />
                                )}
                                {!path.isArchived ? (
                                    <SolidButton
                                        testID="path-archive"
                                        size="sm"
                                        variant="outlined"
                                        type="textSecondary"
                                        iconName="archive-outline"
                                        text="Archive"
                                        disabled={flagMutation.isPending}
                                        onPress={() => showAlert({
                                            title: 'Archive path',
                                            subtitle: 'Archived paths can no longer be started. Open enrollments keep their progress and history.',
                                            cancelText: 'Keep',
                                            confirmText: 'Archive',
                                            onConfirm: () => toggleFlag('isArchived', true),
                                        })}
                                    />
                                ) : (
                                    <SolidButton
                                        testID="path-unarchive"
                                        size="sm"
                                        variant="outlined"
                                        type="textSecondary"
                                        iconName="archive-outline"
                                        text="Unarchive"
                                        disabled={flagMutation.isPending}
                                        onPress={() => toggleFlag('isArchived', false)}
                                    />
                                )}
                            </View>
                        )}

                        <Text style={styles.sectionTitle}>Steps</Text>
                        {steps.length === 0 ? (
                            <Callout type="info" message="This path has no steps. A manager can add them with Edit." />
                        ) : (
                            <View style={styles.stageCard} testID="path-detail-steps">
                                {steps.map((step, sIdx) => (
                                    <StepLine key={step.stepID} step={step} index={sIdx} maps={nameMaps} />
                                ))}
                            </View>
                        )}

                        <View style={styles.participantsHeader}>
                            <Text style={styles.sectionTitle}>Participants</Text>
                            {openCount > 0 && (
                                <Chip icon="people-outline" colorVariant="paths">{`${openCount} active`}</Chip>
                            )}
                            {attentionCount > 0 && (
                                <Chip icon="alert-circle-outline" colorVariant="warning">{`${attentionCount} need${attentionCount === 1 ? 's' : ''} attention`}</Chip>
                            )}
                        </View>
                        <View style={styles.filterWrap}>
                            <SelectInput
                                placeholder="All participants"
                                value={statusFilter}
                                options={STATUS_OPTIONS}
                                hideClearButton
                                hideSelectedPills
                                onUpdate={(v) => setStatusFilter((typeof v === 'object' ? v?.value : v) || '')}
                            />
                        </View>
                        {enrollLoading ? (
                            <View style={styles.loadingWrap}><ActivityIndicator /></View>
                        ) : enrollments.length === 0 ? (
                            <Text style={styles.emptyText}>
                                {statusFilter === 'ALL'
                                    ? 'No one has been started on this path yet.'
                                    : 'No enrollments match this status.'}
                            </Text>
                        ) : (
                            <>
                                {enrollments.map((item) => (
                                    <EnrollmentRow
                                        key={item.enrollmentID}
                                        item={item}
                                        onPress={() => router.push(`/follow-up/enrollments/${item.enrollmentID}`)}
                                    />
                                ))}
                                {hasNextPage && (
                                    <View style={styles.loadMoreWrap}>
                                        {isFetchingNextPage ? (
                                            <ActivityIndicator />
                                        ) : (
                                            <SolidButton
                                                testID="enrollments-load-more"
                                                size="sm"
                                                variant="outlined"
                                                type="secondary"
                                                text="Load More"
                                                onPress={() => fetchNextPage()}
                                            />
                                        )}
                                    </View>
                                )}
                            </>
                        )}
                    </>
                )}
            </ScrollView>
        </View>
    );
}

const styles = StyleSheet.create(({ colors, fonts, shadows }) => ({
    container: { flex: 1 },
    scroll: { flex: 1 },
    content: { padding: 12, paddingBottom: 60, gap: 10, maxWidth: 700, width: '100%', alignSelf: 'center' },
    deniedWrap: { flex: 1, justifyContent: 'center' },
    loadingWrap: { padding: 40, alignItems: 'center' },
    header: { paddingHorizontal: 4, paddingTop: 4, gap: 6 },
    titleRow: { flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
    title: { fontSize: 20, fontFamily: fonts.heading, color: colors.text, flexShrink: 1 },
    chipsRow: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 6 },
    metaLine: { fontSize: 12, color: colors.textSecondary },
    description: { fontSize: 13, color: colors.textSecondary, lineHeight: 18 },
    actionsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
    sectionTitle: { fontSize: 13, fontFamily: fonts.heading, color: colors.textSecondary, marginTop: 6 },
    stageCard: {
        backgroundColor: colors.surface,
        borderRadius: 12,
        padding: 12,
        gap: 8,
        boxShadow: shadows.card,
    },
    stageHeader: { flexDirection: 'row', alignItems: 'center', gap: 8 },
    orderBadge: {
        width: 26,
        height: 26,
        borderRadius: 13,
        backgroundColor: colors.pathsContainer,
        alignItems: 'center',
        justifyContent: 'center',
    },
    orderText: { fontSize: 13, fontFamily: fonts.bold, color: colors.paths },
    stageBody: { flex: 1, minWidth: 0 },
    stageName: { fontSize: 15, fontFamily: fonts.heading, color: colors.text },
    stageDesc: { fontSize: 12, color: colors.textSecondary, marginTop: 1 },
    reqRow: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 10,
        borderTopWidth: 1,
        borderTopColor: colors.border,
        paddingTop: 8,
    },
    reqIcon: {
        width: 28,
        height: 28,
        borderRadius: 7,
        backgroundColor: colors.pathsContainer,
        alignItems: 'center',
        justifyContent: 'center',
    },
    reqBody: { flex: 1, minWidth: 0, gap: 3 },
    reqTitleRow: { flexDirection: 'row', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
    reqName: { fontSize: 13, fontFamily: fonts.bold, color: colors.text, flexShrink: 1 },
    reqMeta: { fontSize: 12, color: colors.textSecondary, lineHeight: 16 },
    participantsHeader: { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 6 },
    filterWrap: { maxWidth: 220 },
    enrollRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        backgroundColor: colors.surface,
        borderRadius: 12,
        padding: 12,
        boxShadow: shadows.card,
        _web: { cursor: 'pointer', _hover: { backgroundColor: colors.hover } },
    },
    enrollBody: { flex: 1, minWidth: 0 },
    enrollName: { fontSize: 14, fontFamily: fonts.bold, color: colors.text },
    enrollMeta: { fontSize: 12, color: colors.textSecondary, marginTop: 1 },
    emptyText: { fontSize: 13, color: colors.textSecondary, fontStyle: 'italic', paddingHorizontal: 4 },
    loadMoreWrap: { alignItems: 'center', paddingVertical: 8 },
}));
